/**
 * vi-normalizer (E14-8) — parser tiếng Việt THUẦN cho AI orchestration (TDD §14b, ai-first 02).
 *
 * Chuyển diễn đạt tiền/ngày của người Việt → giá trị có cấu trúc để confidence-gate kiểm tra.
 * KHÔNG gọi LLM: hàm thuần, test riêng, chạy được như merge-gate ngay từ Phase 1.
 *
 * Nguyên tắc AI-INV (ai-first 01): parser cho ra con số xác định; nếu mơ hồ → trả `null`
 * để tầng confidence-gate hỏi lại (CLARIFY) thay vì đoán. Không bao giờ "đoán bừa" số tiền.
 */

import { rewriteViNumberWords } from "./vi-number-words.js";

/** Đơn vị tiền tệ nội bộ: VND không có phần lẻ → minor unit = đồng (1 VND = 1 minor). */
const VND_MINOR_PER_UNIT = 1n;

/**
 * 🔴 US-AI.F2-0231 đợt B3 (@UC9400 @AI-A11Y-02 @AI-A11Y-15 @SEC-A11Y-01) — BỎ DẤU **GIỮ NGUYÊN VỊ TRÍ**.
 *
 * BUG THẬT: mọi regex ý định (`RECORD_PAID_VERBS`, `BALANCE_KW`…) viết CÓ DẤU ("ghi nhận", "số dư").
 * Người Việt lớn tuổi / gõ nhanh KHÔNG BỎ DẤU: "ghi nhan minh dong 500k", "so du bao nhieu" ⇒ 0 regex
 * nào khớp ⇒ cô Hoa 52 tuổi bị loại khỏi trợ lý (câu ghi tiền rơi thành chit-chat, LLM trả lời chung
 * chung). Đây là rào cản a11y, không phải "nice-to-have".
 *
 * CÁCH LÀM: chuẩn hoá **1 ký tự gốc → 1 ký tự chuẩn** (NFD lấy ký tự nền, bỏ dấu tổ hợp, đ→d, lowercase).
 * ĐỘ DÀI GIỮ NGUYÊN ⇒ chỉ số (index) trên chuỗi chuẩn ÁNH XẠ 1-1 sang chuỗi GỐC ⇒ classifier match trên
 * chuỗi chuẩn nhưng **rút tên người từ chuỗi GỐC** (hiển thị "Minh"/"Hùng" đúng dấu, không thành "Hung").
 * KHÔNG gộp khoảng trắng (gộp sẽ phá ánh xạ vị trí); KHÔNG đụng chữ số / dấu câu ⇒ `parseVndAmountMinor`
 * chạy trên chuỗi gốc vẫn nguyên vẹn.
 */
export function normalizeViText(input: string): string {
  const src = input ?? "";
  let out = "";
  for (const ch of src) {
    // NFD tách ký tự nền + dấu tổ hợp; lấy ký tự nền (bỏ mọi \p{M}). Ký tự không dấu → chính nó.
    const base = ch.normalize("NFD").replace(/\p{M}+/gu, "");
    // "đ"/"Đ" không tách được bằng NFD (ký tự độc lập) → map tay.
    const mapped = base === "đ" || base === "Đ" ? "d" : base;
    const lowered = mapped.toLowerCase();
    // BẤT BIẾN ĐỘ DÀI: chỉ thay khi kết quả cùng số code-unit với ký tự gốc (chữ Việt luôn thoả).
    // Ký tự lạ (emoji, dấu tổ hợp đứng lẻ) → giữ NGUYÊN ký tự gốc ⇒ index không bao giờ lệch.
    out += lowered.length === ch.length ? lowered : ch;
  }
  return out;
}

/**
 * Hậu tố nhân theo nghìn/triệu/tỉ trong cách nói tiếng Việt.
 * @AI-A11Y-02 (B3): CHẤP NHẬN CẢ dạng KHÔNG DẤU ("nghin", "trieu", "cu", "ty") — bác 62 tuổi gõ không
 * dấu vẫn phải ra ĐÚNG số tiền (parser tiền là fact-block server-authoritative, không được thua bàn phím).
 */
const MULTIPLIERS: ReadonlyArray<{ readonly re: RegExp; readonly factor: bigint }> = [
  { re: /^(k|nghìn|nghin|ngàn|ngan)$/i, factor: 1_000n },
  { re: /^(tr|triệu|trieu|củ|cu)$/i, factor: 1_000_000n },
  { re: /^(tỉ|tỷ|ti|ty)$/i, factor: 1_000_000_000n },
];

/**
 * 🔴🔴 US-AI-0774 (@UC0127 · MVP việc 1) — TỪ VỰNG ĐƠN VỊ TIỀN, DÙNG CHUNG.
 *
 * ═══ HAI CÂU HỎI KHÁC NHAU, ĐỪNG GỘP ═══
 *   1. *"Câu này có NÓI VỀ TIỀN không?"*  → bộ định tuyến hỏi, để quyết có mở biểu mẫu ghi không
 *   2. *"Điền SỐ NÀO vào ô?"*            → bộ điền sẵn hỏi, và phải từ chối khi mơ hồ
 *
 * Với *"chi 1 triệu rưỡi tiền sân"*: câu trả lời (1) là **CÓ** — đây rõ ràng là câu ghi chi, phải
 * mở biểu mẫu. Câu trả lời (2) là **KHÔNG BIẾT** — ô tiền để trống, biểu mẫu hỏi.
 *
 * Gộp hai câu hỏi lại thì sinh ra một trong hai lỗi: hoặc từ chối mở biểu mẫu cho một câu rõ ràng
 * là chi (mất năng lực), hoặc điền một con số đoán (sai tiền). Vì thế thứ dùng chung ở đây là
 * **TỪ VỰNG**, không phải hàm tính giá trị.
 *
 * ═══ VÌ SAO PHẢI DÙNG CHUNG ═══
 * `turn-router.EXPENSE_DECLARE_KW` từng chép tay một tập con: `k|nghin|ngan|tr|trieu|dong|d`.
 * Thiếu `tỷ`, `củ`, `trăm` ⇒ đo 13/09: `chi 1 ty tien san` và `chi 1 cu tien san` **không mở được
 * biểu mẫu nào**, dù bộ đọc số hiểu đúng cả hai. 5/14 cách viết bị lệch như vậy.
 *
 * ⚠️ Nguồn là [MULTIPLIERS] + `trăm` + ký hiệu đồng — **không chép tay lần nữa**. Thêm một đơn vị
 * vào `MULTIPLIERS` thì chuỗi này tự có, và ca `@UC0127` đối chiếu hai bên.
 */
export const MONEY_UNIT_WORDS: readonly string[] = Object.freeze([
  ...MULTIPLIERS.flatMap((m) =>
    m.re.source
      .replace(/^\^\(|\)\$$/g, "")
      .split("|")
      .map((w) => w.trim())
      .filter((w) => w.length > 0),
  ),
  "trăm",
  "tram",
  "đồng",
  "dong",
  "đ",
  "d",
]);

/** Mẫu "một con số + đơn vị tiền" — dùng cho câu hỏi (1): *câu này có nói về tiền không*. */
export const MONEY_AMOUNT_HINT_RE = new RegExp(
  `\\d[\\d.,]*\\s*(?:${MONEY_UNIT_WORDS.join("|")})\\b|\\d{3,}`,
  "iu",
);

/**
 * 🔴🔴 US-AI-0739 (@UC0103 · sổ 10/09 **QĐ-10**) — *"2 trăm"* ở Việt Nam **LUÔN là 200.000**.
 *
 * `trăm` KHÔNG vào `MULTIPLIERS` được, vì nó là đơn vị DUY NHẤT có **hai** trị số tuỳ ngữ cảnh:
 *
 * | Cách nói | Kết quả | Vì sao |
 * |---|---|---|
 * | `2 trăm nghìn` · `2 trăm triệu` | **parse được** (×100 rồi ×đơn-vị-sau) | không còn chỗ hiểu khác |
 * | **`2 trăm`** (không gì theo sau) | **`null` ⇒ CLARIFY** | 200 hay 200.000? |
 *
 * ═══ 🔴🔴 VÌ SAO `2 trăm` VẪN LÀ `null` — TÔI ĐÃ CÀI SAI MỘT LƯỢT RỒI SỬA ═══
 *
 * Bản đầu của tôi cho `trăm` đứng một mình = ×100.000 ⇒ *"2 trăm"* ra thẳng 200.000 và biểu mẫu MỞ.
 * **Ba ca đỏ**, và chúng đúng: `CLAUDE.md` §4 liệt kê **đúng chuỗi này** làm ví dụ zero-tolerance —
 * *"'2 trăm' (200 hay 200.000?) = case zero-tolerance ⇒ bắt buộc CLARIFY, không đoán."*
 *
 * Đọc kỹ lại QĐ-10 thì người dùng **không** đòi bỏ bước hỏi. Họ viết *"Tôi đồng ý nên hỏi lại,
 * **nhưng** nguyên tắc thêm là ở VN '2 trăm' luôn là 200.000"*, và §Nghiệm-thu của chính QĐ ấy ghi
 * *"⇒ **hỏi lại**, và lựa chọn **đầu tiên** là 200.000 đ"* + *"Nghịch 4: vẫn KHÔNG tự chọn rồi đi tiếp"*.
 *
 * ⇒ Yêu cầu thật là **đổi CÂU HỎI**, không phải bỏ câu hỏi: vẫn clarify, nhưng đề xuất
 * **200.000 trước** thay vì bày hai lựa chọn ngang nhau. Việc ấy làm ở tầng dựng lượt
 * (`buildRecordClarify` + `suggestHundredShorthandMinor` ngay dưới), KHÔNG ở parser tiền.
 *
 * Parser tiền là chỗ **quyết định có ghi hay không**; nới nó là nới một bất biến §4 — `CLAUDE.md` §6
 * ghi rõ: *"phải nới một invariant để pass test ⇒ tuyệt đối không"*.
 */
const HUNDRED_RE = /^(trăm|tram)$/i;
/** `trăm` có đơn vị theo sau = chữ số hàng trăm bình thường. */
const HUNDRED_FACTOR = 100n;

/**
 * Phân tích chuỗi tiền tiếng Việt → số tiền VND ở minor units (bigint), hoặc `null` nếu không chắc.
 *
 * Hỗ trợ: "500k", "1 củ", "1,5tr", "1.5tr", "2 triệu", "150000", "1.500.000đ", "1,500,000", "3 tỷ", "nửa triệu", "một phẩy năm triệu".
 * Dấu phân cách: luật S29 ở `parseViNumberScaledBy1000` (đọc theo đơn vị bậc; hai quy ước ra hai số ⇒ `null`).
 * Trả `null` khi: rỗng, không có chữ số, dấu phân cách mơ hồ, hoặc phần thập phân không chia hết thành đồng nguyên.
 */
/**
 * 🔴🔴 US-AI-0773 (@UC0126) — «RƯỠI» ĐỨNG SAU MỘT SỐ TIỀN ⇒ CÂU MƠ HỒ. MỘT LUẬT, MỘT CHỖ.
 *
 * ═══ VÌ SAO HÀM NÀY TỒN TẠI, thay vì một phép kiểm thứ ba ═══
 * Repo có **BA** đường đọc số tiền từ câu người dùng (lịch sử v1 — sau sunset chỉ còn (1) và `extractMoneyCandidates`
 * ở `money-parser.ts`, thay (2)+(3); cả hai gọi `rewriteViNumberWords` ở đầu cho số viết bằng chữ, US-AIV2-0110 S18):
 *   1. `parseVndAmountMinor` — đọc MỘT token đứng riêng (chặt nhất)
 *   2. `extractAmount` (form-prefill) — quét token trong câu, điền vào biểu mẫu
 *   3. `extractAmountMinor` (intent-classifier) — quét token trong câu, dùng cho ai-chat
 *
 * US-AI-0772 vá "rưỡi" ở đường (2). Đo lại 13/09 thì đường (3) **vẫn hỏng**:
 *   `ghi nhan An da dong 1 trieu ruoi` ⇒ `record_paid`, amountMinor = **1.000.000**
 * — và giá trị đó đi thẳng vào câu xác nhận *"đã đóng 1.000.000 đ phải không?"* rồi vào đề xuất
 * ghi sổ (`ai-chat.service.ts:396,420` — v1, đã xoá). Người nói 1,5 triệu.
 *
 * ⇒ Vá lần thứ ba bằng cách chép phép kiểm sang file thứ ba là **tái tạo đúng căn bệnh**. Luật ở
 * đây, hai nơi kia GỌI. Thêm một đường đọc thứ tư mai sau cũng chỉ cần gọi một dòng.
 *
 * ═══ KHÔNG tính ×1,5 ═══
 * `1 triệu rưỡi` thì rõ, nhưng `500 rưỡi` thì không — 550 hay 500.500? Luật ca `"2 trăm"` (§4):
 * mơ hồ ⇒ HỎI, không đoán.
 */
const HALF_AFTER_AMOUNT_RE =
  /\d[\d.,]*\s*(?:k|tr|trieu|triệu|ty|tỷ|cu|củ|nghin|nghìn|ngan|ngàn|đ|d)?\s*(?:rưỡi|rưởi|ruoi)(?=$|[^\p{L}])/iu;

/** Câu có «rưỡi» đứng sau một số tiền ⇒ `true` (mơ hồ, phải hỏi lại). */
export function hasAmbiguousHalfAmount(text: string): boolean {
  return HALF_AFTER_AMOUNT_RE.test(text ?? "");
}

export function parseVndAmountMinor(input: string): bigint | null {
  // 🔴 US-AIV2-0110 S18 (@UC0262): cụm số CHỮ ⇒ chữ số trước (cùng hàm với `extractMoneyCandidates` — một cơ chế); cụm có từ bậc
  // mà không đọc được thành MỘT cách ⇒ `null` (mơ hồ ⇒ tầng trên hỏi lại).
  const words = rewriteViNumberWords(input);
  if (words.ambiguousRun !== null || words.unreadRun !== null) return null;
  const raw = words.text.trim().toLowerCase();
  if (raw === "") return null;

  // Tách phần số và hậu tố đơn vị (k/tr/củ/triệu/tỷ/đ/vnd...). Cho phép khoảng trắng ở giữa.
  // \p{L} = mọi ký tự chữ (gồm dấu tiếng Việt: nghìn, ngàn, triệu, tỷ...).
  // 🔴 US-AI-0739 — đơn vị nay có thể GHÉP HAI TỪ ("trăm nghìn", "trăm triệu") ⇒ cho phép khoảng
  // trắng bên trong phần chữ. Regex cũ `(\p{L}*)$` không khớp chuỗi có dấu cách ⇒ *"2 trăm nghìn"*
  // rơi thẳng xuống `null` (đo được trên `develop`, xem `tram-multiplier.test.ts` — v1, đã xoá).
  const match = raw.match(/^([\d.,]+)\s*([\p{L}\s]*)$/u);
  if (!match) return null;

  const numPart = match[1] ?? "";
  const unitRaw = match[2] ?? "";
  // Bỏ ký hiệu tiền tệ "đồng/dong/đ/d/vnd/vnđ" để còn lại đơn vị nhân (k/tr/...) hoặc rỗng.
  // @AI-A11Y-02 (B3): người gõ không dấu viết "200000d" / "500k dong" ⇒ phải bỏ được cả dạng không dấu.
  const stripped = unitRaw.replace(/đồng|dong|vnđ|vnd|đ/gu, "").replace(/\s+/gu, " ").trim();
  // "200000d" (không dấu) ⇒ còn lại "d" = ký hiệu đồng ⇒ coi như KHÔNG có đơn vị nhân (×1).
  const unit = stripped === "d" ? "" : stripped;

  const factor = resolveMultiplier(unit);
  if (factor === null) return null;

  // Quan trọng: KHÔNG dùng float ở đường money (ADR-0002 "CẤM float"). Parse phần nguyên +
  // phần thập phân thành BigInt riêng, scale ×1000 hoàn toàn bằng số nguyên. Dấu phân cách đọc THEO ĐƠN VỊ (S29).
  // Không bậc: có ký hiệu đồng ("1,5 đồng", "12.000,00 đ") hay TRẦN ("20.00" — giờ, rà đối kháng S29) — hai luật khác nhau.
  const mode: NumberMode = factor > 1n ? "magnitude" : unitRaw.trim() !== "" ? "currency" : "bare";
  const scaled = parseViNumberScaledBy1000(numPart, mode);
  if (scaled === null) return null;

  // scaled là (giá trị ×1000). Nhân factor rồi quy về đồng nguyên (chia 1000).
  const totalUnitsScaledBy1000 = scaled;
  const minor = (totalUnitsScaledBy1000 * factor * VND_MINOR_PER_UNIT) / 1000n;

  // Nếu sau khi nhân factor vẫn còn phần lẻ < 1 đồng → coi như mơ hồ (vd "1,5k" = 1500 ok;
  // còn số tạo ra fraction đồng không nguyên → null).
  if ((totalUnitsScaledBy1000 * factor) % 1000n !== 0n) return null;
  return minor >= 0n ? minor : null;
}

/**
 * 🔴🔴 US-AI-0739 (@UC0103 · sổ 10/09 **QĐ-10**) — CÁCH ĐỌC ĐỀ XUẤT cho *"N trăm"*.
 *
 * > *"Ở Việt Nam thì '2 trăm' luôn là 200.000."* — QĐ người dùng 10/09
 *
 * Hàm này **KHÔNG** quyết định số tiền. Nó chỉ trả về *cách đọc nên mời trước* để lượt CLARIFY hỏi
 * một câu có trọng tâm (*"ý bạn là 200.000 đ?"*) thay vì bày hai lựa chọn ngang nhau. Người dùng
 * vẫn phải gật — `parseVndAmountMinor("2 trăm")` vẫn `null`, nên không đường nào ghi tiền đi qua đây.
 *
 * ⚠️ Tách khỏi parser là CÓ CHỦ ĐÍCH: parser gác *"có được ghi không"*, hàm này gác *"hỏi thế nào"*.
 * Gộp lại thì một ngày nào đó ai đó dùng nó để điền thẳng vào form, và bất biến §4 mất mà không ai
 * thấy. Trả `null` khi không phải dạng *"N trăm"* trần.
 */
export function suggestHundredShorthandMinor(text: string): bigint | null {
  const norm = text.trim().toLowerCase();
  // `trăm` phải là từ CUỐI của cụm tiền: "2 trăm" ⇒ có; "2 trăm nghìn" ⇒ không (đã parse được rồi).
  const m = norm.match(/(?<![\d.,])(\d[\d.,]*)\s*(trăm|tram)(?![\p{L}\d])/u);
  if (!m) return null;
  const after = norm.slice(m.index! + m[0].length).trimStart();
  if (/^(nghìn|nghin|ngàn|ngan|triệu|trieu|tr|củ|cu|tỉ|tỷ|ti|ty)\b/u.test(after)) return null;
  const scaled = parseViNumberScaledBy1000(m[1] ?? "", "magnitude");
  if (scaled === null) return null;
  const minor = (scaled * 100_000n * VND_MINOR_PER_UNIT) / 1000n;
  return minor > 0n ? minor : null;
}

/**
 * Parse số kiểu VN thành BigInt đã scale ×1000 — THUẦN số nguyên, không float. `hasMagnitude` = số đứng trước một ĐƠN VỊ
 * BẬC (k · nghìn · tr · triệu · củ · tỷ · "trăm …"): dấu phân cách có nghĩa khác nhau tuỳ vế này.
 *
 * 🔴🔴 US-AIV2-0110 S29 (@UC0281) — LUẬT DẤU PHÂN CÁCH. Hai quy ước cùng sống trên bàn phím người Việt: kiểu Việt (`.` nhóm
 * nghìn, `,` thập phân — chính `formatVndMinor` in "200.000 đ" từ QĐ PO 01/10) và kiểu Anh (ngược lại — bàn phím/locale tiếng Anh). Luật chỉ
 * ĐỌC khi hai quy ước ra CÙNG một số; khác nhau ⇒ `null` ⇒ tầng trên HỎI (CLAUDE.md §4 — ca "2 trăm"):
 *
 * | Dạng | Kết quả | Vì sao |
 * |---|---|---|
 * | `1.5tr` · `1,5tr` · `0.5 triệu` · `1,05 triệu` (MỘT dấu, **1–2** chữ số sau, có bậc) | **thập phân** | nhóm nghìn luôn đúng 3 chữ số ⇒ chỉ còn một cách hiểu |
 * | `1.500 triệu` · `1,500 triệu` · `2.500k` (MỘT dấu, **đúng 3** chữ số sau, có bậc) | `null` | 1.500 triệu (nhóm) hay 1,5 triệu (thập phân)? lệch 1.000× |
 * | `1.500` (không bậc) | 1.500 | nhóm nghìn kiểu Việt; thập phân kiểu Anh = 1,5 đồng — vô nghĩa |
 * | `1,500` · `100,000` (không bậc) | `null` | 100.000 kiểu Anh hay 100 đồng kiểu Việt? (develop từng ra **100 đ**) |
 * | `1.500.000` · `1,500,000` (≥2 dấu CÙNG loại, mỗi nhóm đúng 3) | nhóm nghìn | hai dấu không thể cùng là dấu thập phân |
 * | `20.00` · `1,5` · `150.00đ` · `1,5 đồng` (KHÔNG bậc; MỘT dấu + 1–2 chữ số sau dấu) | `null` | "20.00" là GIỜ; "150.00đ" là gõ thiếu "0" (150.000đ) — rà vòng 5 |
 * | `12.000,00 đ` · `12,000.00 VND` (trộn dấu ĐÚNG khuôn kế toán, có ký hiệu đồng) | 12.000 | mỗi khuôn chỉ hợp lệ theo một quy ước |
 * | trộn `.` với `,` khác · nhóm sai độ dài · nhóm mở bằng `0` · >3 chữ số thập phân | `null` | không đoán |
 *
 * 🔴 **QĐ PO 27/09/2026 — đọc "1.5tr"/"1.5 triệu" = 1.500.000, ĐẢO QĐ agent US-AI-0771 (@UC0124, 13/09) `1.5tr` ⇒ `null`.** Lý do:
 * cái sai thật của 0771 là ĐỌC `1.5` THÀNH `15` (bản cũ xoá mọi dấu chấm ⇒ 15.000.000) — sai đó VẪN bị chặn; `1.5` + bậc chỉ còn
 * MỘT cách hiểu là 1,5; mọi dạng còn hai cách hiểu VẪN hỏi (`1.500 triệu` · `2.500k` · `100,000` · `150.00đ`); và phiếu xác nhận
 * in con số trước khi ghi. Đừng "sửa lại cho an toàn" về `null` mà không hỏi PO. Phần lẻ dưới 1 đồng ("1,5 đồng") bị chặn ở người gọi (`% 1000`).
 */
/** Số đứng trước: một ĐƠN VỊ BẬC ("k", "triệu") · chỉ KÝ HIỆU ĐỒNG ("đ", "vnd") · không gì (TRẦN). */
type NumberMode = "magnitude" | "currency" | "bare";

function parseViNumberScaledBy1000(s: string, mode: NumberMode): bigint | null {
  const hasMagnitude = mode === "magnitude";
  const t = s.trim();
  // Chỉ chữ số và dấu phân cách, mở và đóng bằng chữ số (dấu câu dính đuôi "200.000," đã được bộ quét câu tách ra).
  if (!/^\d(?:[\d.,]*\d)?$/u.test(t)) return null;
  const hasDot = t.includes(".");
  const hasComma = t.includes(",");
  if (!hasDot && !hasComma) return BigInt(t) * 1000n;
  if (hasDot && hasComma) {
    // Khuôn kế toán đầy đủ, chỉ khi có ký hiệu đồng: "12.000,00 đ" (Việt) · "12,000.00 VND" (Anh). Mọi hình trộn khác ⇒ null.
    if (mode !== "currency") return null;
    const m = /^([1-9]\d{0,2}(?:\.\d{3})+),(\d{1,2})$/u.exec(t) ?? /^([1-9]\d{0,2}(?:,\d{3})+)\.(\d{1,2})$/u.exec(t);
    if (m === null) return null;
    return BigInt(m[1]!.replace(/[.,]/gu, "")) * 1000n + BigInt(m[2]!.padEnd(3, "0"));
  }
  const sep = hasDot ? "." : ",";
  const parts = t.split(sep);
  if (parts.length > 2) {
    // ≥2 dấu cùng loại ⇒ chỉ có thể là nhóm nghìn: đầu nhóm 1–3 chữ số (không mở bằng 0), các nhóm sau đúng 3.
    if (!/^[1-9]\d{0,2}$/u.test(parts[0]!) || parts.slice(1).some((g) => !/^\d{3}$/u.test(g))) return null;
    return BigInt(parts.join("")) * 1000n;
  }
  const [intPart = "", fracPart = ""] = parts;
  if (fracPart.length === 3) {
    // MỘT dấu + đúng 3 chữ số: nhóm nghìn hay thập phân? Chỉ "1.500" KHÔNG bậc là một cách hiểu (xem bảng).
    if (hasMagnitude || sep !== "." || !/^[1-9]\d{0,2}$/u.test(intPart)) return null;
    return BigInt(intPart + fracPart) * 1000n;
  }
  // 🔴 S29b (rà vòng 5, H1): MỘT dấu + 1–2 chữ số chỉ là thập phân TRƯỚC BẬC. Không bậc ("150.00đ" · "300.00 dong" · "20.00d") ⇒
  // `null`: người gõ nhiều khả năng THIẾU một số 0 (150.000đ) — bản S29 đầu đọc 150 đ, thiếu 1.000 lần. Khuôn kế toán đầy đủ
  // ("12.000,00 đ") đi nhánh trộn-dấu ở trên, không qua đây.
  if (fracPart.length < 1 || fracPart.length > 2 || mode !== "magnitude") return null;
  // 1–2 chữ số sau dấu ⇒ thập phân (",5" → 500 phần nghìn; ",05" → 050).
  return BigInt(intPart) * 1000n + BigInt(fracPart.padEnd(3, "0"));
}

/**
 * Đơn vị rỗng → ×1; k/tr/củ/tỷ → factor tương ứng; đơn vị lạ → null (mơ hồ).
 *
 * 🔴 US-AI-0739 (@UC0103) — nay nhận **đơn vị GHÉP** (`trăm nghìn`, `trăm triệu`). Luật `trăm`:
 * đứng một mình ⇒ ×100.000 (nuốt chữ "nghìn"); có đơn vị theo sau ⇒ ×100 rồi nhân tiếp.
 *
 * ⚠️ Vẫn **fail-closed**: bất kỳ từ nào ngoài bảng ⇒ `null` ⇒ tầng trên HỎI LẠI. *"2 trăm rưỡi"*
 * (250k, cách nói có thật) vì thế vẫn không bị đoán — đúng §4 *"zero-tolerance, không đoán"*.
 */
function resolveMultiplier(unit: string): bigint | null {
  if (unit === "") return 1n;

  const words = unit.split(" ").filter((w) => w !== "");
  if (words.length === 0) return 1n;
  // Chỉ `trăm` mới ghép được; mọi cụm hai từ khác vẫn là mơ hồ.
  if (words.length > 2) return null;
  if (words.length === 2 && !HUNDRED_RE.test(words[0]!)) return null;

  let factor = 1n;
  for (const [i, word] of words.entries()) {
    if (HUNDRED_RE.test(word)) {
      // 🔴 `trăm` là từ CUỐI ⇒ *"2 trăm"* ⇒ MƠ HỒ (200 hay 200.000?) ⇒ null, để tầng trên HỎI LẠI.
      //    Có đơn vị theo sau ⇒ hàng trăm bình thường, nhân tiếp.
      if (i === words.length - 1) return null;
      factor *= HUNDRED_FACTOR;
      continue;
    }
    const hit = MULTIPLIERS.find((m) => m.re.test(word));
    if (!hit) return null;
    factor *= hit.factor;
  }
  return factor;
}

/** Kết quả phân giải ngày tương đối: số ngày lệch so với hôm nay (0 = hôm nay, -1 = hôm qua). */
export interface RelativeDay {
  readonly offsetDays: number;
}

/**
 * Phân giải diễn đạt ngày tương đối tiếng Việt → offset ngày so với "hôm nay", hoặc `null`.
 *
 * Thuần & không dùng `Date.now()` (để test xác định): trả offset, caller tự cộng vào mốc của mình.
 * Hỗ trợ: "hôm nay", "hôm qua", "hôm kia", "ngày mai", "ngày kia". "mùng 5"/ngày cụ thể → null
 * (cần lịch tháng → để confidence-gate hỏi lại, không đoán).
 */
export function parseRelativeDay(input: string): RelativeDay | null {
  // US-AIV2-0022: so trên chuỗi ĐÃ BỎ DẤU + gộp khoảng trắng — "hom qua" / "hôm  qua" đều là hôm qua
  // (@AI-A11Y-02: bàn phím không dấu không được làm mất một ngày). Khoá ở bảng không dấu, MỘT nguồn.
  const raw = normalizeViText(input ?? "").replace(/\s+/gu, " ").trim();
  // `Object.hasOwn`, không tra `[raw]` trần: "constructor"/"__proto__" là khoá của prototype chain (review #815 M1 đo ra
  // ngày "0NaN-NaN-NaN" resolved) — không hiểu thì phải `null`, không phải rác.
  if (!Object.hasOwn(RELATIVE_DAY_OFFSETS, raw)) return null;
  return { offsetDays: RELATIVE_DAY_OFFSETS[raw]! };
}

/** Bảng ngày tương đối, KHOÁ KHÔNG DẤU (đầu vào được `normalizeViText` trước khi tra). */
const RELATIVE_DAY_OFFSETS: Readonly<Record<string, number>> = Object.freeze({
  "hom nay": 0,
  "bua nay": 0,
  "hom qua": -1,
  "hom kia": -2,
  "ngay mai": 1,
  mai: 1,
  "ngay kia": 2,
  "ngay mot": 2,
  mot: 2,
});
